import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.addEventListener('click', this.#handleUpsellAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    window.removeEventListener('pageshow', this.#handlePageShow);
    this.removeEventListener('click', this.#handleUpsellAdd);
  }


  #upsellAdding = false;

  #handleUpsellAdd = async (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-dd-upsell-add]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;
    event.preventDefault();
    if (this.#upsellAdding || button.disabled) return;

    const variantId = Number(button.dataset.variantId);
    const status = button.closest('.dd-cart-drawer-upsell-action')?.querySelector('[role="status"]');
    if (!Number.isSafeInteger(variantId) || variantId <= 0) {
      if (status) status.textContent = 'Please open the fragrance page to select a sample.';
      return;
    }

    this.#upsellAdding = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = 'Adding…';

    try {
      const sections = [...new Set(
        Array.from(document.querySelectorAll('cart-items-component'))
          .map((component) => component.dataset.sectionId)
          .filter(Boolean)
      )];
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add this item. Please try again.');
      }
      if (status) status.textContent = 'Added';
      this.dispatchEvent(new CartAddEvent(result, String(variantId), {
        source: 'cart-drawer-upsell',
        itemCount: 1,
        variantId: String(variantId),
        sections: result.sections,
      }));
    } catch (error) {
      if (status) status.textContent = error instanceof Error ? error.message : 'Unable to add this item. Please try again.';
    } finally {
      this.#upsellAdding = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Refreshes the cart/header section when this page is restored from the
   * browser back-forward cache. Without this, the restored drawer can contain
   * stale empty-cart HTML even though Shopify's server-side cart has items.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;

    const cartItems = this.querySelector('cart-items-component');
    const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : undefined;
    if (!sectionId) return;

    sectionRenderer.renderSection(sectionId, { cache: false }).catch((error) => {
      console.error('Failed to refresh cart after history navigation:', error);
    });
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
